import { useEffect, useMemo, useState } from "react";
import { BoxContentsProvider, FbaBatchStatus, FbaShipmentStatus, TransportType } from "@boxem/core-api-schema";

import styles from "./ReviewShipmentsPage.module.css";

import { getBillOfLadingV2, getShippingLabelsV2 } from "@/api/fulfillment";
import { usePrinterContext } from "@/contexts/PrinterContext";
import { FbaBatch, FbaShipment } from "@/contexts/ShipmentContext";
import { useUserContext } from "@/contexts/UserContext";
import LoadingSpinner from "@/components/LoadingSpinner/LoadingSpinner";
import PageContent from "@/components/PageContent";
import Button from "@/components/forms/Button";
import InfoBanner from "@/components/InformationBanner/InfoBanner";
import Select from "@/components/forms/Select";
import IconShipment from "@/components/icons/IconShipment";
import Tooltip from "@/components/Tooltip";
import IconStop from "@/components/icons/IconStop";
import IconCaution from "@/components/icons/IconCaution";
import IconCheckCircle from "@/components/icons/IconCheckCircle";

import { getReadableStatus, shipmentStatusDescription } from "../../utils";
import { FbaPageProps } from "../FbaShipmentFlow";
import Box from "../ShipmentBox";
import { useLocation, useSearchParams } from "wouter";
import Flex from "@/components/Flex/Flex";
import IconCopyClipboard from "@/components/icons/IconCopyClipboard";
import IconPrintGoogle from "@/components/icons/IconPrintGoogle";
import { LabelsModal, LabelsModalItem } from "@/components/LabelsModal/LabelsModal";
import Card, { CardContent } from "@/components/Card";
import IconDownloadBox from "@/components/icons/IconDownloadBox";
import dayjs from "dayjs";

enum LabelPrintingOption {
  OnlyFBA,
  OnlyUPS,
  UPSAndFBA,
};

const readablePrintLabelText = (option: LabelPrintingOption, isLTL: boolean = false) => {
  switch (option) {
    case LabelPrintingOption.OnlyFBA:
      return isLTL ? "Print FBA Box Labels" : "Print Only FBA Box Labels";
    case LabelPrintingOption.OnlyUPS:
      return "Print Only UPS Labels";
    case LabelPrintingOption.UPSAndFBA:
      return "Print UPS & FBA Box Labels";
  }
};

const focusedShipmentFromQuery: (batch: FbaBatch, queryShipment: string|null) => FbaShipment = (batch, queryShipment) => {
  const n = parseInt(queryShipment);
  return batch.shipments.find(s => s.id === n) ?? batch.shipments[0];
};

export default function ReviewShipmentsPage(props: FbaPageProps) {
  const { addressBook } = useUserContext();
  const [query, setQuery] = useSearchParams();
  const [location, navigate] = useLocation();
  const { printFBABoxLabels, submitJobInProgress, handleSPAPIShippingLabelPDF } = usePrinterContext();
  const { batch, onCancelClicked, onError, cloneShipment } = props;

  const [labelsModalItems, setLabelsModalItems] = useState<LabelsModalItem[]>([]);
  const [labelRequestLoading, setLabelRequestLoading] = useState<boolean>(false);
  const [printLabelOption, setPrintLabelOption] = useState<LabelPrintingOption>(() => (
    batch.shipmentType === TransportType.LTL
      ? LabelPrintingOption.OnlyFBA // LTL can only do box labels - there's no UPS label(s)
      : LabelPrintingOption.UPSAndFBA
  ));
  const [bol, setBOL] = useState<string>(null);

  const focusedShipment = focusedShipmentFromQuery(batch, query.get('shipment'));
  const setFocusedShipment = (shipment: FbaShipment) => {
    setQuery(prev => {
      prev.set("shipment", shipment.id.toString());
      return prev;
    });
  };

  useEffect(() => {
    setBOL(null);
    (async () => {
      const bol = await getBillOfLadingV2(focusedShipment.id);
      setBOL(bol.url);
    })();
  }, [focusedShipment.id]);

  const handleViewBOL = () => {
    const a = document.createElement('a');
    a.href = bol;
    a.target = "_blank";
    a.download = "bol.pdf";
    a.click();
  };

  const cancelled = (shipment: FbaShipment) =>
    shipment.status === FbaShipmentStatus.TRANSPORT_VOIDING
    || shipment.status === FbaShipmentStatus.TRANSPORT_VOIDED
    || shipment.status === FbaShipmentStatus.CANCELLED;

  const fromAddress = addressBook.find(address => address.id === batch.fromId);
  const shipmentBoxes = useMemo(() => batch.boxes.filter(box => box.shipmentId === focusedShipment?.id), [focusedShipment?.id]);
  const carrierName = useMemo(() => focusedShipment.transportationOptions?.[0]?.carrier.name, [focusedShipment]);
  const carrierDate = useMemo(() => dayjs(focusedShipment.transportationOptions?.[0]?.carrierAppointment?.startTime).format("ddd MMMM D"), [focusedShipment]);

  const totalNumberOfUnits: {[key: number]: number} = useMemo(() => {
    const totalNumberOfUnits = {};
    for (let i = 0; i < batch.shipments.length; i++) {
      const shipment = batch.shipments[i];
      for (const item of shipment.items) {
        totalNumberOfUnits[shipment.id] = (totalNumberOfUnits[shipment.id] ?? 0) + item.quantity;
      }
    }
    return totalNumberOfUnits;
  }, [batch.shipments]);

  const totalBoxWeight: {[key: number]: number} = useMemo(() => {
    const totalBoxWeight = {};
    for (let i = 0; i < batch.shipments.length; i++) {
      const shipment = batch.shipments[i];
      for (const box of shipment.boxes) {
        // sometimes the shipment boxes do not appear to have the weight initialized.
        const batchBox = batch.boxes.find(b => b.id === box.id);
        totalBoxWeight[shipment.id] = (totalBoxWeight[shipment.id] ?? 0) + batchBox.weight;
      }
    }
    return totalBoxWeight;
  }, [focusedShipment?.id]);

  const shipmentStatusSentiment = (() => {
    switch (focusedShipment?.status) {
      case FbaShipmentStatus.CANCELLED:
      case FbaShipmentStatus.DECLINED:
      case FbaShipmentStatus.CLOSED:
      case FbaShipmentStatus.ERROR:
        return { className: "inline-notice", icon: <IconCaution /> };
      default:
        return { className: "inline-positive", icon: <IconCheckCircle /> };
    }
  })();

  const renderShipmentOption = (className?: string) => (option: FbaShipment, index: number) => (
    <span className={className}>
      {cancelled(option) &&
        <>
          <Tooltip title="This shipment has been cancelled. You can still view it, but no further actions can be taken.">
            <span className={styles.icon}><IconStop style={{color: "var(--sentiment-text-negative)"}} /></span>
          </Tooltip>
          &nbsp;
        </>
      }
      <span className={"strong"}>{option.shipmentName} ({index + 1}/{batch.shipments.length})</span>
    </span>
  );

  const onlyPrintFBABoxLabels = () => {
    printFBABoxLabels(
      focusedShipment,
      fromAddress,
      shipmentBoxes,
      batch.boxContentsProvider === BoxContentsProvider.Barcode2D,
      batch,
    );
  };

  const onlyPrintUpsBoxLabels = () => {
    setLabelRequestLoading(true);
    getShippingLabelsV2(focusedShipment.id)
      .then(handleSPAPIShippingLabelPDF)
      .catch(onError)
      .finally(() => setLabelRequestLoading(false));
  };

  const printBothUpsAndFbaLabels = () => {
    setLabelRequestLoading(true);
    getShippingLabelsV2(focusedShipment.id)
      .then(result => {
        handleSPAPIShippingLabelPDF(result, {
          shipment: focusedShipment,
          fromAddress,
          do2DBarcode: batch.boxContentsProvider === BoxContentsProvider.Barcode2D,
        }, batch);
      })
      .catch(onError)
      .finally(() => setLabelRequestLoading(false));
  };

  const handlePrintLabels = () => {
    switch (printLabelOption) {
      case LabelPrintingOption.OnlyFBA:
        onlyPrintFBABoxLabels();
        break;
      case LabelPrintingOption.OnlyUPS:
        onlyPrintUpsBoxLabels();
        break;
      case LabelPrintingOption.UPSAndFBA:
        printBothUpsAndFbaLabels();
        break;
      default:
    }
  };

  return (
    <PageContent className={styles.pageContent}>
      <div className={`${styles.header} ${styles.row}`}>
        <h1>Review Shipments</h1>
        <span className="fs-small-heading">
          Status:&nbsp;&nbsp;
          <Tooltip
            title={shipmentStatusDescription(batch.shipmentType, focusedShipment?.status)}
            delayMs={0}
          >
            <span className={shipmentStatusSentiment.className}>
              {shipmentStatusSentiment.icon}&nbsp;{getReadableStatus(focusedShipment?.status)}
            </span>
        </Tooltip>
        </span>
        <span className={styles.spacer} />
        <Button
          type="secondary"
          onClick={() => navigate("/shipments")}
        >
          <IconShipment />&nbsp;
          Track Shipments
        </Button>
        <Button type="danger" onClick={onCancelClicked}>Cancel All Shipments</Button>
      </div>
      <InfoBanner
        type="positive"
        storageKey="reviewShipmentInfoKey"
        message={
          <>
            <span className="strong">Your shipment is complete!&nbsp;</span>
            It has been submitted and accepted by Amazon. You can reprint your labels on this page if needed.
            This page will continue to update as we receive updates from Amazon.
          </>
        }
      />
      <div className={styles.row}>
        <div className={styles.leftPane}>
          <Select
            fullWidth={true}
            options={batch.shipments}
            value={focusedShipment}
            optionRender={renderShipmentOption()}
            selectionRender={renderShipmentOption(styles.selection)}
            onChange={setFocusedShipment}
          />
        </div>
        <div className={styles.rightPane}>
          <Flex justify="end" gap={2}>
            <Button
              type="secondary"
              onClick={() => cloneShipment(focusedShipment.id)}
            >
              <IconCopyClipboard />
              Clone Shipment
            </Button>

            <Button
              type="secondary"
              onClick={() => {
                const items: LabelsModalItem[] = [];
                for (const shipmentItem of focusedShipment.items) {
                  items.push({
                    batchItem: shipmentItem.batchItem,
                    totalQuantity: shipmentItem.quantity,
                    assignedQuantity: shipmentItem.quantity,
                    fnsku: shipmentItem.fnSku,
                  });
                }
                setLabelsModalItems(items);
              }}
            >
              <IconPrintGoogle />
              Print
            </Button>

            {batch.shipmentType === TransportType.LTL && (
              <Tooltip title={
                `Please note that the Bill of Lading may take a few minutes to generate after your shipment is confirmed.
                It will be available no later than the morning of the scheduled pickup.`
              }>
                <Button onClick={handleViewBOL} disabled={!bol}>
                  <IconDownloadBox />&nbsp;BOL
                </Button>
              </Tooltip>
            )}
          </Flex>
        </div>
      </div>
      <div className={styles.row}>
        <div className={styles.leftPane}>
          {shipmentBoxes.map((box, i) => (
            <Box
              key={i}
              box={box}
              shipment={focusedShipment}
            />
          ))}
        </div>
        <div className={styles.rightPane}>
          <Card className={styles.shipmentSummary}>
            {batch.shipmentType === TransportType.LTL && (
              <CardContent borderBottom="solid">
                <span className="strong">Carrier: </span>{carrierName}
                &nbsp;&nbsp;&nbsp;
                <span className="strong">Date: </span>{carrierDate}
              </CardContent>
            )}
            <CardContent className={styles.shippingDetails}>
              <div className={`${styles.row} ${styles.heading}`}>Shipping details</div>
              <div className={styles.row}>
                <img className={styles.shippingIcon} src={"/images/from.svg"} alt="shipping from icon" />
                <div>
                  <div className="small-text" style={{margin: "0.1em 0 0.8em"}}>Ship from</div>
                  <div className="strong small-text">{fromAddress?.name}</div>
                  <div className="weak small-text" style={{marginBottom: "20px"}}>
                    {fromAddress?.address1}{fromAddress?.address2 ? ` ${fromAddress?.address2}` : ""}, {
                    fromAddress?.city}, {fromAddress?.state} {fromAddress?.zipcode}
                  </div>
                </div>
              </div>
              <div className={styles.dashes} />
              <div className={styles.row}>
                <img className={styles.shippingIcon} src={"/images/to.svg"} alt="shipping to icon" />
                <div>
                  <div className="small-text" style={{margin: "0.1em 0 0.8em"}}>Ship to</div>
                  <div className="strong small-text">Amazon {focusedShipment?.fulfillmentCenterId} Warehouse</div>
                  <div className="weak small-text">
                    {focusedShipment?.destinationAddress?.address1}{focusedShipment?.destinationAddress?.address2 ? ` ${focusedShipment?.destinationAddress?.address2}` : ""}, {
                    focusedShipment?.destinationAddress?.city}, {focusedShipment?.destinationAddress?.state} {focusedShipment?.destinationAddress?.zipcode}
                  </div>
                </div>
              </div>
            </CardContent>
            <CardContent className={styles.shipmentContents} borderTop="solid" borderBottom="none">
              <div className={`${styles.row} ${styles.heading}`}>Shipment contents</div>
              <div className={styles.grid}>
                {batch.shipmentType === TransportType.LTL && (
                  <>
                    <span>Total number of Pallets</span>
                    <span>{focusedShipment?.pallets.length}</span>
                  </>
                )}
                <span>Total number of boxes</span>
                <span>{shipmentBoxes.length}</span>
                <span>Total number of SKUs</span>
                <span>{focusedShipment?.items.length}</span>
                <span>Total number of units</span>
                <span>{totalNumberOfUnits[focusedShipment?.id]}</span>
                <span>Total weight of all boxes combined</span>
                <span>{totalBoxWeight[focusedShipment?.id]}&nbsp;lbs</span>
              </div>
            </CardContent>
            <CardContent className={styles.printLabels}>
              {batch.status !== FbaBatchStatus.BATCH_CANCELLED && (
                <>
                  {batch.shipmentType === TransportType.SPD &&
                    <Select
                      value={printLabelOption}
                      onChange={option => setPrintLabelOption(option)}
                      optionRender={option => readablePrintLabelText(option)}
                      selectionRender={option => readablePrintLabelText(option)}
                      options={[ LabelPrintingOption.UPSAndFBA, LabelPrintingOption.OnlyFBA, LabelPrintingOption.OnlyUPS ]}
                      fullWidth={true}
                      size="large"
                    />
                  }
                  <Button
                    className={styles.printLabelsButton}
                    type="primary"
                    size="large"
                    disabled={submitJobInProgress || labelRequestLoading}
                    onClick={handlePrintLabels}
                  >
                    {submitJobInProgress
                      ? <LoadingSpinner color="white" size="24px" />
                      : readablePrintLabelText(printLabelOption, batch.shipmentType === TransportType.LTL)
                    }
                  </Button>
                </>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
      <LabelsModal
        open={labelsModalItems?.length > 0}
        onClose={() => setLabelsModalItems([])}
        items={labelsModalItems}
      />
    </PageContent>
  );
}
